import { useState, useEffect, useCallback } from 'react';

import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';
import { BsX, BsInfoCircle, BsCheckCircle, BsXCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { LAYER } from '#lib/constants/layers';

const EXIT_ANIMATION_MS = 300;

const TYPE_STYLES = {
	error: {
		icon: BsXCircle,
		surface: 'bg-warning-200 border-warning-500/30',
		iconBg: 'bg-warning-500',
		text: 'text-warning-700',
	},
	info: {
		icon: BsInfoCircle,
		surface: 'bg-primary-100 border-primary-200',
		iconBg: 'bg-primary-500',
		text: 'text-primary-700',
	},
	success: {
		icon: BsCheckCircle,
		surface: 'bg-success-200 border-success-500/30',
		iconBg: 'bg-success-700',
		text: 'text-success-700',
	},
};

const Toast = ({
	title,
	message,
	type = 'error',
	onClose,
	autoClose = false,
	autoCloseDuration = 10000,
	actionButton,
	showCloseButton = true,
}) => {
	const [isVisible, setIsVisible] = useState(true);
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);

	const handleClose = useCallback(() => {
		setIsAnimatingOut(true);
		setTimeout(() => {
			setIsVisible(false);
			onClose?.();
		}, EXIT_ANIMATION_MS);
	}, [onClose]);

	useEffect(() => {
		if (!autoClose || autoCloseDuration <= 0) return undefined;
		const timer = setTimeout(handleClose, autoCloseDuration);
		return () => clearTimeout(timer);
	}, [autoClose, autoCloseDuration, handleClose]);

	if (!isVisible) return null;

	const styles = TYPE_STYLES[type] || TYPE_STYLES.error;
	const Icon = styles.icon;

	return createPortal(
		<div
			role={type === 'error' ? 'alert' : 'status'}
			className={`fixed bottom-6 right-6 ${LAYER.toast} w-96 max-w-[calc(100vw-3rem)] transition-all duration-300 ease-out ${
				isAnimatingOut ? 'opacity-0 translate-y-4 scale-95' : 'opacity-100 translate-y-0 scale-100'
			}`}
		>
			<div className={`flex items-start gap-4 p-5 border rounded-card shadow-elevated ${styles.surface}`}>
				<div
					className={`flex-shrink-0 w-10 h-10 rounded-control flex items-center justify-center ${styles.iconBg}`}
				>
					<Icon className="w-5 h-5 text-white" />
				</div>

				<div className={`flex-1 min-w-0 pt-0.5 ${styles.text}`}>
					{title && <h4 className="text-md-bold mb-1">{title}</h4>}
					<p className="text-sm">{message}</p>

					{actionButton && (
						<div className="mt-4">
							<Button
								onClick={actionButton.onClick}
								variant={type === 'error' ? 'danger' : 'solid'}
								size="sm"
							>
								{actionButton.label}
							</Button>
						</div>
					)}
				</div>

				{showCloseButton && (
					<button
						type="button"
						onClick={handleClose}
						aria-label="Schließen"
						className="flex-shrink-0 w-8 h-8 rounded-control text-bgBase-800 hover:text-text hover:bg-white/60 flex items-center justify-center transition-colors"
					>
						<BsX className="w-5 h-5" />
					</button>
				)}
			</div>
		</div>,
		document.getElementById('portal-root'),
	);
};

Toast.propTypes = {
	title: PropTypes.string,
	message: PropTypes.string.isRequired,
	type: PropTypes.oneOf(['error', 'info', 'success']),
	onClose: PropTypes.func,
	autoClose: PropTypes.bool,
	autoCloseDuration: PropTypes.number,
	actionButton: PropTypes.shape({
		label: PropTypes.string.isRequired,
		onClick: PropTypes.func.isRequired,
	}),
	showCloseButton: PropTypes.bool,
};

export default Toast;
